import React from 'react';
import moment from 'moment';

import { ChatSession } from '../../../layouts/MainLayout/slice/types';
import {
  bucketDailyCounts,
  filterByRange,
  getMostActiveDay,
  percentDelta,
  sortRecent,
  splitCurrentPrevious,
} from '../utils/analytics';
import { TIME_RANGES, TimeRangeKey } from '../slice/constants';

type Params = {
  chatSessions: ChatSession[];
  callSessions: ChatSession[];
  range: TimeRangeKey;
};

export function useAdminDashboardAnalytics({ chatSessions, callSessions, range }: Params) {
  const { now, allSessions, scopedAll, scopedChat, scopedCall } = React.useMemo(() => {
    const now = moment();
    const all = [...chatSessions, ...callSessions];
    const scopedAll = filterByRange(all, range, now);
    const scopedChat = filterByRange(chatSessions, range, now);
    const scopedCall = filterByRange(callSessions, range, now);
    return { now, allSessions: all, scopedAll, scopedChat, scopedCall };
  }, [chatSessions, callSessions, range]);

  const allDailyPoints = React.useMemo(() => bucketDailyCounts(scopedAll, range, now), [scopedAll, range, now]);
  const chatDailyPoints = React.useMemo(() => bucketDailyCounts(scopedChat, range, now), [scopedChat, range, now]);
  const callDailyPoints = React.useMemo(() => bucketDailyCounts(scopedCall, range, now), [scopedCall, range, now]);
  const mostActive = React.useMemo(() => getMostActiveDay(allDailyPoints), [allDailyPoints]);
  const recent = React.useMemo(() => sortRecent(scopedAll, 10), [scopedAll]);

  const totalSessionsDelta = React.useMemo(() => {
    const { current, previous } = splitCurrentPrevious(allSessions, range, now);
    return percentDelta(current.length, previous.length);
  }, [allSessions, range, now]);

  const chatsDelta = React.useMemo(() => {
    const { current, previous } = splitCurrentPrevious(chatSessions, range, now);
    return percentDelta(current.length, previous.length);
  }, [chatSessions, range, now]);

  const callsDelta = React.useMemo(() => {

 
    const { current, previous } = splitCurrentPrevious(callSessions, range, now);
    console.log('callSessions', callSessions);
    return percentDelta(current.length, previous.length);
  }, [callSessions, range, now]);

  const activeDays = React.useMemo(() => {
    const days = new Set(scopedAll.map((s) => moment(s.createdAt).format('YYYY-MM-DD')));
    return days.size;
  }, [scopedAll]);

  const callShare = React.useMemo(() => {
    const total = scopedAll.length;
    if (!total) return 0;
    return Math.round((scopedCall.length / total) * 100);
  }, [scopedAll.length, scopedCall.length]);

  const allChartValues = React.useMemo(() => allDailyPoints.map((p) => p.value), [allDailyPoints]);
  const chatChartValues = React.useMemo(() => chatDailyPoints.map((p) => p.value), [chatDailyPoints]);
  const callChartValues = React.useMemo(() => callDailyPoints.map((p) => p.value), [callDailyPoints]);

  const rangeLabel = range === TIME_RANGES.WEEK ? 'Last 7 days' : range === TIME_RANGES.MONTH ? 'Last 30 days' : 'Last 24 hours';

  return {
    now,
    allSessions,
    scopedAll,
    scopedChat,
    scopedCall,
    allDailyPoints,
    mostActive,
    recent,
    totalSessionsDelta,
    chatsDelta,
    callsDelta,
    activeDays,
    callShare,
    allChartValues,
    chatChartValues,
    callChartValues,
    rangeLabel,
  };
}

